import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Link2, Share2, PenLine } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import useLenis from "@/hooks/useLenis";
import api, { mediaUrl } from "@/lib/api";
import { fetchAllArticles } from "@/pages/ArticlesPage";
import RichBody from "@/components/site/RichBody";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
  catch { return ""; }
};
const readTime = (body) => Math.max(1, Math.round((body || "").split(/\s+/).filter(Boolean).length / 200));

function ShareBar({ title }) {
  const url = window.location.href;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.error("Could not copy"); }
  };
  const native = async () => {
    if (navigator.share) { try { await navigator.share({ title, url }); } catch { /* cancelled */ } }
    else copy();
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="article-share-bar">
      <span className="text-[11px] uppercase tracking-[0.25em] text-imi-text/40 mr-2">Share</span>
      <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer" data-testid="share-x"
        className="h-10 px-4 border border-imi-border text-imi-text/60 hover:border-imi-gold hover:text-imi-gold transition-colors flex items-center text-xs uppercase tracking-[0.15em]">X</a>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer" data-testid="share-facebook"
        className="h-10 px-4 border border-imi-border text-imi-text/60 hover:border-imi-gold hover:text-imi-gold transition-colors flex items-center text-xs uppercase tracking-[0.15em]">Facebook</a>
      <button onClick={copy} data-testid="share-copy" className="h-10 px-4 border border-imi-border text-imi-text/60 hover:border-imi-gold hover:text-imi-gold transition-colors flex items-center gap-2 text-xs uppercase tracking-[0.15em]"><Link2 size={14} /> Copy link</button>
      <button onClick={native} data-testid="share-native" className="h-10 px-4 border border-imi-border text-imi-text/60 hover:border-imi-gold hover:text-imi-gold transition-colors flex items-center gap-2 text-xs uppercase tracking-[0.15em]"><Share2 size={14} /> Share</button>
    </div>
  );
}

export default function ArticlePage() {
  useLenis();
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [more, setMore] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    setA(null); setNotFound(false);
    api.get(`/content/${id}`).then((r) => setA(r.data)).catch(() => setNotFound(true));
    fetchAllArticles().then((all) => setMore(all.filter((x) => x.id !== id && (x.body || "").trim()).slice(0, 3))).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, [id]);

  const cover = a ? mediaUrl(a) : null;

  return (
    <div className="bg-imi-bg text-imi-text min-h-screen">
      <Header />
      <main className="pt-28 pb-24">
        <div className="max-w-[840px] mx-auto px-6 md:px-10">
          <Link to="/articles" data-testid="back-articles" className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-imi-text/50 hover:text-imi-gold transition-colors">
            <ArrowLeft size={16} /> All articles
          </Link>

          {notFound && (
            <div className="py-32 text-center" data-testid="article-not-found">
              <PenLine size={40} className="text-imi-gold/30 mx-auto mb-4" />
              <div className="font-serif text-2xl">Article not found</div>
            </div>
          )}

          {a && (
            <article className="mt-10" data-testid="article-page">
              {a.category && <span className="text-imi-gold text-[10px] uppercase tracking-[0.3em] border border-imi-gold/40 px-3 py-1.5">{a.category}</span>}
              <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="article-title">{a.title}</h1>
              {a.excerpt && <p className="mt-6 text-imi-text/60 text-lg md:text-xl leading-relaxed font-serif italic">{a.excerpt}</p>}
              <div className="mt-6 flex flex-wrap items-center gap-3 text-imi-text/45 text-xs uppercase tracking-[0.2em]">
                <span>{settings?.founder_name || "Harold Cunningham"}</span>
                <span className="text-imi-gold/50">·</span>
                <span>{fmtDate(a.created_at)}</span>
                <span className="text-imi-gold/50">·</span>
                <span>{readTime(a.body)} min read</span>
              </div>

              {cover && (
                <div className="mt-10 border border-imi-border overflow-hidden">
                  <img src={cover} alt={a.title} className={(a.body || "").trim() ? "w-full max-h-[560px] object-cover" : "w-full h-auto"} data-testid="article-cover" />
                </div>
              )}

              <div className="mt-12">
                {(a.body || "").trim() ? <RichBody text={a.body} /> : (a.external_url || a.cta_url) ? (
                  <div className="border border-imi-gold/30 bg-imi-surface p-6" data-testid="article-external">
                    <p className="text-imi-text/70">{a.excerpt || "This piece was published by an outside outlet."}</p>
                    <a href={a.external_url || a.cta_url} target="_blank" rel="noopener noreferrer" data-testid="article-read-original" className="mt-4 inline-flex items-center gap-2 bg-imi-gold text-imi-bg px-5 py-3 text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-imi-goldlight transition-colors">
                      Read the original{a.meta ? ` at ${a.meta}` : ""}
                    </a>
                  </div>
                ) : null}
              </div>

              <div className="mt-14 pt-8 border-t border-imi-border">
                <ShareBar title={a.title} />
              </div>
            </article>
          )}

          {more.length > 0 && (
            <div className="mt-20" data-testid="more-articles">
              <div className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-6">Keep reading</div>
              <div className="divide-y divide-imi-border border-y border-imi-border">
                {more.map((m) => (
                  <Link key={m.id} to={`/articles/${m.id}`} data-testid={`more-article-${m.id}`} className="group flex items-center justify-between gap-6 py-6">
                    <div>
                      <h3 className="font-serif text-xl md:text-2xl group-hover:text-imi-gold transition-colors">{m.title}</h3>
                      <div className="mt-1 text-imi-text/40 text-[10px] uppercase tracking-[0.25em]">{fmtDate(m.created_at)} · {readTime(m.body)} min read</div>
                    </div>
                    <ArrowRight className="text-imi-text/40 group-hover:text-imi-gold group-hover:translate-x-1 transition-all shrink-0" size={18} />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer settings={settings} />
    </div>
  );
}
